import { useEffect, useState } from 'react';

import { getContact as getContactUntyped } from '~/components/Contact';
import type { ContactWithPrimaryEmailAndPhone } from '~/components/Contacts/ContactUtils';

// Contact.jsx is untyped JS, so TS infers `orgId: null` from its default parameter. Re-declare the
// signature the function actually has.
const getContact = getContactUntyped as unknown as (
  contactId: number,
  claim: null,
  orgId: number
) => Promise<ContactWithPrimaryEmailAndPhone | undefined>;

export interface UseContactNamesResult {
  /** Full name by contact id. A contact whose name could not be had is absent rather than blank. */
  namesById: Record<number, string>;
  /** True until a lookup of the current id set has settled. */
  isLoading: boolean;
}

interface ResolvedNames {
  /** The id set these names answer. */
  idsKey: string;
  namesById: Record<number, string>;
}

const fetchContactNames = async (organizationId: number, contactIds: number[]): Promise<Record<number, string>> => {
  const namesById: Record<number, string> = {};
  await Promise.all(
    contactIds.map(async (contactId) => {
      try {
        const contact = await getContact(contactId, null, organizationId);
        // An empty name is no name: it would render a blank cell where the caller's fallback is meant to show.
        if (contact?.full_name) {
          namesById[contactId] = contact.full_name;
        }
      } catch {
        // `getContact` rejects on a failed GET and reports nothing. The name is a label, not data the
        // caller needs to work, so the id is left out for the caller's fallback to stand in for.
      }
    })
  );
  return namesById;
};

/** The full names of the contacts a rule table assigns to, one request per distinct id. A changed id
 *  set is looked up afresh, and until it settles `namesById` keeps what the previous set resolved:
 *  blanking every name back to its fallback on each add or delete would flicker the whole column. */
export const useContactNames = (organizationId: number, contactIds: number[]): UseContactNamesResult => {
  // The effect's key rather than the array: a caller that builds `contactIds` inline hands over a new
  // array on every render, and the same ids reordered or repeated are not a change.
  const idsKey = [...new Set(contactIds)].sort((a, b) => a - b).join(',');
  const [resolved, setResolved] = useState<ResolvedNames>({ idsKey: '', namesById: {} });

  useEffect(() => {
    if (idsKey === '') {
      return undefined;
    }
    let cancelled = false;
    void fetchContactNames(organizationId, idsKey.split(',').map(Number)).then((namesById) => {
      // The ids may have changed, or the component unmounted, while the requests were in flight: a
      // late answer for the old set must not replace the names of the one that superseded it.
      if (!cancelled) {
        setResolved({ idsKey, namesById });
      }
    });
    return () => {
      cancelled = true;
    };
  }, [organizationId, idsKey]);

  return { namesById: resolved.namesById, isLoading: idsKey !== '' && resolved.idsKey !== idsKey };
};
